import React, { useEffect, useState } from 'react';
import { portalService, PortalProfile } from '../services/portalService';

const PortalProfilePage: React.FC = () => {
    const [profile, setProfile] = useState<PortalProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editMode, setEditMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Edit form state
    const [formData, setFormData] = useState({
        address: '',
        phone: '',
        mobile: '',
        email: '',
    });

    useEffect(() => {
        portalService
            .getProfile()
            .then(setProfile)
            .catch((err) => setError(err.response?.data?.message || 'No pudimos cargar tu perfil. Por favor, inténtalo de nuevo más tarde.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (profile) {
            const p = profile.patient;
            setFormData({
                address: p.address || '',
                phone: p.phone || '',
                mobile: p.mobile || '',
                email: p.email || profile.user?.email || '',
            });
        }
    }, [profile]);

    const handleEditClick = () => {
        setEditMode(true);
        setSaveSuccess(false);
        setSaveError(null);
    };

    const handleCancelClick = () => {
        setEditMode(false);
        setSaveSuccess(false);
        setSaveError(null);
        // Reset form data
        if (profile) {
            const p = profile.patient;
            setFormData({
                address: p.address || '',
                phone: p.phone || '',
                mobile: p.mobile || '',
                email: p.email || profile.user?.email || '',
            });
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveError(null);
        setSaveSuccess(false);

        try {
            const updated = await portalService.updateProfile(formData);
            setProfile(updated);
            setEditMode(false);
            setSaveSuccess(true);
        } catch (err: any) {
            setSaveError(err.response?.data?.message || 'No pudimos guardar los cambios. Por favor, inténtalo de nuevo.');
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    if (!profile) {
        return (
            <div className="alert alert-warning">
                No pudimos encontrar la información de tu perfil. Por favor, contacta a la clínica si el problema persiste.
            </div>
        );
    }

    const p = profile.patient;

    return (
        <div className="portal-profile">
            <h2 className="mb-4">
                <i className="fas fa-user-circle me-2"></i>Mi Perfil
            </h2>

            {/* Success/Error feedback */}
            {saveSuccess && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                    Perfil actualizado exitosamente.
                    <button type="button" className="btn-close" onClick={() => setSaveSuccess(false)}></button>
                </div>
            )}
            {saveError && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {saveError}
                    <button type="button" className="btn-close" onClick={() => setSaveError(null)}></button>
                </div>
            )}

            <div className="card">
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Información Personal</h5>
                    {!editMode ? (
                        <button className="btn btn-sm btn-light" onClick={handleEditClick}>
                            <i className="fas fa-edit me-1"></i>Editar
                        </button>
                    ) : (
                        <div className="btn-group">
                            <button
                                className="btn btn-sm btn-primary"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1"></span>
                                        Guardando...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-check me-1"></i>Guardar
                                    </>
                                )}
                            </button>
                            <button
                                className="btn btn-sm btn-secondary"
                                onClick={handleCancelClick}
                                disabled={saving}
                            >
                                Cancelar
                            </button>
                        </div>
                    )}
                </div>
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="fw-bold">Nombre</label>
                            <p>{p.first_name} {p.last_name}</p>
                        </div>
                        <div className="col-md-6">
                            <label className="fw-bold">Identificación</label>
                            <p>{p.identification || '—'}</p>
                        </div>
                        {editMode ? (
                            <>
                                <div className="col-md-6">
                                    <label className="fw-bold">Correo Electrónico</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="fw-bold">Teléfono</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="fw-bold">Celular</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="mobile"
                                        value={formData.mobile}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="fw-bold">Dirección</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="col-md-6">
                                    <label className="fw-bold">Correo Electrónico</label>
                                    <p>{p.email || profile.user?.email || '—'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="fw-bold">Teléfono</label>
                                    <p>{p.phone || '—'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="fw-bold">Celular</label>
                                    <p>{p.mobile || '—'}</p>
                                </div>
                            </>
                        )}
                        <div className="col-md-6">
                            <label className="fw-bold">Fecha de Nacimiento</label>
                            <p>{p.birth_date || '—'}</p>
                        </div>
                        {!editMode && (
                            <div className="col-md-6">
                                <label className="fw-bold">Dirección</label>
                                <p>{p.address || '—'}</p>
                            </div>
                        )}
                        <div className="col-md-6">
                            <label className="fw-bold">Estado</label>
                            <p>
                                <span className={`badge ${p.status === 'A' ? 'bg-success' : 'bg-secondary'}`}>
                                    {p.status === 'A' ? 'Activo' : 'Inactivo'}
                                </span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PortalProfilePage;